import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QueryObjectsModel, QueryOutputMode } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { _AbcTreeNode } from '@barthauer/abc/ui-components';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import { ResultsService } from '../../../services/result.service';
import { Property } from '../../../viewModels/abc-queries-editor.viewModel';
import { NestedGridComponent } from './nested-grid/nested-grid.component';

@Component({
    selector: 'abc-opath-queries-editor-results-table',
    templateUrl: './results-table.component.html',
    styleUrls: ['./results-table.component.scss'],
    standalone: false
})
export class ResultsTableComponent implements OnInit, OnDestroy {
  @Input() resultsEmpty: boolean;
  @ViewChild('nestedGrid') nestedGrid: NestedGridComponent;

  public data: Object = null;
  public query: QueryObjectsModel = null;
  public loading: boolean = false;
  public messageBoxVisible: boolean = false;
  public translation: {
    executionFailed: string;
    loading: string;
    resultMessage: {
      clickMessage: string;
      button: string;
      viewMessage: string;
    };
  };

  private queries: _AbcTreeNode<Property>;
  private resAsync: Promise<ResourceSet>;
  private $unsubscribe: Subject<void> = new Subject();

  constructor(
    private resultService: ResultsService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private resourcesService: ResourceManagerService,
    private messageBoxService: MessageBoxService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    this.translation = {
      executionFailed: (await this.resAsync).get('result-ExecutionError'),
      loading: (await this.resAsync).get('result-loading'),
      resultMessage: {
        button: (await this.resAsync).get('menu-executeAsTable'),
        clickMessage: (await this.resAsync).get('result-ExecutePart1'),
        viewMessage: (await this.resAsync).get('result-ExecutePart2'),
      },
    };

    this.data = this.resultService.resultTableCache;
  }

  public async refreshQueriesData(): Promise<void> {
    this.loading = true;
    this.queries = this.abcQueriesEditorService.queries;
    if (this.queries.children.length) {
      await this.resultService.createObjectQueries(this.queries, this.abcQueriesEditorService.queryDocument);
      this.resultService.queryDocument
        .pipe(
          take(1),
          switchMap((data: QueryObjectsModel) => {
            this.query = data;
            return this.resultService.executeQueries({ ...data, OutputMode: QueryOutputMode.Json });
          })
        )
        .subscribe(
          (data: Object) => {
            this.data = data;
            if (this.data) {
              this.resultsEmpty = false;
            }

            this.resultService.resultTableCache = data;
            this.loading = false;
          },
          async (response: HttpErrorResponse) => {
            !this.messageBoxVisible && (await this.showErrorMessage(response));
            this.resultService.resultTableCache = null;
          }
        );
    } else {
      this.loading = false;
    }
  }

  private async showErrorMessage(response: HttpErrorResponse): Promise<void> {
    this.messageBoxVisible = true;
    this.data = null;
    this.loading = false;
    const settings = new MessageSettings();
    settings.mode = MessageMode.Error;
    settings.icon = MessageBoxIcon.Error;
    settings.button = MessageBoxButtons.OK;
    settings.message = response.error.Message;
    if (response.error.InnerException) {
      settings.errorMessage =
        response.error.InnerException.Message +
        `\n\n` +
        response.error.InnerException.ExceptionType +
        `\n\n` +
        response.error.InnerException.ExceptionMessage;
    }

    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((result: DialogResult) => {
        if (DialogResult.OK === result['result']) {
          this.messageBoxVisible = false;
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next(null);
    this.$unsubscribe.complete();
  }
}
